/**
 * Opening Book System
 *
 * Stores and retrieves optimal opening moves for Reversi/Othello.
 * Contains theory-based opening sequences and proven lines.
 */

import { BoardPosition, PlayerColor } from '@/types/game-types';
import type { GameState } from '@/core/models/GameState';

/**
 * Opening book entry
 */
export interface OpeningEntry {
  readonly position: BoardPosition;
  readonly score: number;        // Theoretical evaluation
  readonly frequency: number;    // How often this move is played
  readonly winRate: number;      // Success rate (0-1)
  readonly depth: number;        // How deep into the opening this is
  readonly comment?: string;     // Human-readable description
  readonly variations?: OpeningEntry[]; // Follow-up moves
}

/**
 * Opening book key (board position hash)
 */
type OpeningKey = string;

/**
 * Named opening sequence
 */
export interface OpeningSequence {
  readonly name: string;
  readonly description: string;
  readonly moves: BoardPosition[];
  readonly evaluation: number;
  readonly isMainLine: boolean;
}

/**
 * Opening Book Database
 */
export class OpeningBook {
  private readonly _entries = new Map<OpeningKey, OpeningEntry[]>();
  private readonly _sequences: OpeningSequence[] = [];
  private readonly _maxDepth: number;

  constructor(maxDepth = 12) {
    this._maxDepth = maxDepth;
    this.initializeOpeningDatabase();
  }

  /**
   * Get the best move from opening book
   */
  getOpeningMove(gameState: GameState): BoardPosition | null {
    const key = this.generateKey(gameState);
    const entries = this._entries.get(key);

    if (!entries || entries.length === 0) {
      return null;
    }

    // Filter valid moves
    const validEntries = entries.filter(entry =>
      gameState.validMoves.has(entry.position)
    );

    if (validEntries.length === 0) {
      return null;
    }

    // Select best move based on score and win rate
    const bestEntry = validEntries.reduce((best, current) => {
      const bestValue = best.score * 0.7 + best.winRate * 0.3;
      const currentValue = current.score * 0.7 + current.winRate * 0.3;
      return currentValue > bestValue ? current : best;
    });

    return bestEntry.position;
  }

  /**
   * Get all opening moves with scores
   */
  getOpeningMoves(gameState: GameState): OpeningEntry[] {
    const key = this.generateKey(gameState);
    const entries = this._entries.get(key) || [];

    return entries.filter(entry =>
      gameState.validMoves.has(entry.position)
    );
  }

  /**
   * Check if position is in opening book
   */
  hasOpeningMove(gameState: GameState): boolean {
    const key = this.generateKey(gameState);
    const entries = this._entries.get(key);
    return entries ? entries.some(entry => gameState.validMoves.has(entry.position)) : false;
  }

  /**
   * Get opening depth (how many moves into theory we are)
   */
  getOpeningDepth(gameState: GameState): number {
    let depth = 0;
    const totalPieces = gameState.scores.black + gameState.scores.white;
    const moveCount = totalPieces - 4; // Subtract initial 4 pieces

    return Math.min(moveCount, this._maxDepth);
  }

  /**
   * Get opening sequence by name
   */
  getSequence(name: string): OpeningSequence | null {
    return this._sequences.find(seq => seq.name === name) || null;
  }

  /**
   * Get all available sequences
   */
  getAllSequences(): OpeningSequence[] {
    return [...this._sequences];
  }

  /**
   * Initialize the opening database with known theory
   */
  private initializeOpeningDatabase(): void {
    // Standard starting position: Black plays first after initial setup
    // Initial board: center 4 squares occupied, Black to move

    // Move 1: Black's first move options
    this.addOpening('eeeeeeeeeeeeeeeeeeeeeeeeeeee12212100eeeeeeeeeeeeeeeeeeeeeeeeeeee', [
      { pos: 19, score: 0, freq: 0.3, win: 0.52, comment: 'Tiger' },      // d3
      { pos: 26, score: 0, freq: 0.25, win: 0.51, comment: 'Rabbit' },    // c4
      { pos: 37, score: 0, freq: 0.25, win: 0.51, comment: 'Cat' },       // f5
      { pos: 44, score: 0, freq: 0.2, win: 0.50, comment: 'Ox' },         // e6
    ]);

    // Tiger opening (d3): Most aggressive
    this.addOpening('eeeeeeeeeeeeeeeeeeee1eeeeee12111100eeeeeeeeeeeeeeeeeeeeeeeeeeee', [
      { pos: 18, score: 2, freq: 0.4, win: 0.53, comment: 'Tiger main line' }, // c3
      { pos: 20, score: 0, freq: 0.3, win: 0.52, comment: 'Tiger solid' },     // e3
      { pos: 11, score: -1, freq: 0.2, win: 0.49, comment: 'Tiger passive' },  // d2
      { pos: 34, score: 1, freq: 0.1, win: 0.51, comment: 'Tiger counter' },   // c5
    ]);

    // Rabbit opening (c4): Balanced
    this.addOpening('eeeeeeeeeeeeeeeeeeeeeeeeeeee11112100eeeeeeeeeeeeeeeeeeeeeeeeeeee', [
      { pos: 18, score: 1, freq: 0.35, win: 0.52, comment: 'Rabbit main' },    // c3
      { pos: 19, score: 1, freq: 0.35, win: 0.52, comment: 'Rabbit parallel' }, // d3
      { pos: 25, score: 0, freq: 0.2, win: 0.50, comment: 'Rabbit edge' },     // b4
      { pos: 33, score: -1, freq: 0.1, win: 0.48, comment: 'Rabbit weak' },    // b5
    ]);

    // Cat opening (f5): Solid
    this.addOpening('eeeeeeeeeeeeeeeeeeeeeeeeeeee12211100eeeee1eeeeeeeeeeeeeeeeeeeeee', [
      { pos: 45, score: 2, freq: 0.4, win: 0.54, comment: 'Cat main line' },   // f6
      { pos: 43, score: 1, freq: 0.3, win: 0.52, comment: 'Cat solid' },       // d6
      { pos: 29, score: 0, freq: 0.2, win: 0.50, comment: 'Cat transpose' },   // f4
      { pos: 52, score: -1, freq: 0.1, win: 0.48, comment: 'Cat edge' },       // e7
    ]);

    // Ox opening (e6): Defensive
    this.addOpening('eeeeeeeeeeeeeeeeeeeeeeeeeeee12211100eeeeeeeeeeee1eeeeeeeeeeeeeee', [
      { pos: 43, score: 1, freq: 0.4, win: 0.52, comment: 'Ox main' },         // d6
      { pos: 45, score: 1, freq: 0.3, win: 0.52, comment: 'Ox symmetric' },    // f6
      { pos: 37, score: 0, freq: 0.2, win: 0.50, comment: 'Ox transpose' },    // f5
      { pos: 52, score: -1, freq: 0.1, win: 0.48, comment: 'Ox passive' },     // e7
    ]);

    // Add deeper sequences for main lines
    this.addDeepSequences();

    // Add named sequences
    this.addNamedSequences();
  }

  /**
   * Add deeper opening sequences (6-12 moves)
   */
  private addDeepSequences(): void {
    // Tiger main line continuation
    this.addOpening('eeeeeeeeeeeeeeeeee11eeeeeee11111100eeeeeeeeeeeeeeeeeeeeeeeeeeee', [
      { pos: 10, score: 3, freq: 0.5, win: 0.55, comment: 'Tiger sharp' },     // c2
      { pos: 12, score: 2, freq: 0.3, win: 0.53, comment: 'Tiger solid' },     // e2
      { pos: 25, score: 1, freq: 0.2, win: 0.51, comment: 'Tiger positional' }, // b4
    ]);

    // Advanced middle game transitions
    this.addOpening('eeeeeeee11eeeeeeee11eeeeeee11111100eeeeeeeeeeeeeeeeeeeeeeeeeeee', [
      { pos: 9, score: 2, freq: 0.4, win: 0.54, comment: 'Advanced tiger' },   // b2
      { pos: 17, score: 2, freq: 0.3, win: 0.53, comment: 'Edge control' },    // b3
      { pos: 24, score: 1, freq: 0.3, win: 0.52, comment: 'Center play' },     // a4
    ]);
  }

  /**
   * Add named opening sequences
   */
  private addNamedSequences(): void {
    this._sequences.push(
      {
        name: 'Tiger Opening',
        description: 'Aggressive opening focusing on quick development',
        moves: [19, 18, 10], // d3, c3, c2
        evaluation: 2,
        isMainLine: true,
      },
      {
        name: 'Rabbit Opening',
        description: 'Balanced opening with flexible development',
        moves: [26, 18, 19], // c4, c3, d3
        evaluation: 1,
        isMainLine: true,
      },
      {
        name: 'Cat Defense',
        description: 'Solid defensive setup',
        moves: [37, 45, 43], // f5, f6, d6
        evaluation: 1,
        isMainLine: true,
      },
      {
        name: 'Ox System',
        description: 'Conservative approach with emphasis on stability',
        moves: [44, 43, 45], // e6, d6, f6
        evaluation: 0,
        isMainLine: true,
      },
      {
        name: 'Buffalo Opening',
        description: 'Hypermodern approach delaying central confrontation',
        moves: [19, 20, 12], // d3, e3, e2
        evaluation: 1,
        isMainLine: false,
      },
      {
        name: 'Snake Variation',
        description: 'Provocative opening leading to complex positions',
        moves: [26, 25, 33], // c4, b4, b5
        evaluation: 0,
        isMainLine: false,
      }
    );
  }

  /**
   * Helper method to add opening positions
   */
  private addOpening(
    boardString: string,
    moves: Array<{ pos: number; score: number; freq: number; win: number; comment?: string }>
  ): void {
    const entries: OpeningEntry[] = moves.map(move => ({
      position: move.pos,
      score: move.score,
      frequency: move.freq,
      winRate: move.win,
      depth: this.calculateDepth(boardString),
      comment: move.comment,
    }));

    this._entries.set(boardString, entries);
  }

  /**
   * Generate position key from game state
   */
  private generateKey(gameState: GameState): string {
    // Convert board to string representation
    // 'e' = empty, '1' = black, '2' = white
    return Array.from(gameState.board).map(cell => {
      switch (cell) {
        case PlayerColor.EMPTY: return 'e';
        case PlayerColor.BLACK: return '1';
        case PlayerColor.WHITE: return '2';
        default: return 'e';
      }
    }).join('');
  }

  /**
   * Calculate opening depth from board string
   */
  private calculateDepth(boardString: string): number {
    const pieceCount = boardString.split('').filter(c => c !== 'e').length;
    return Math.max(0, pieceCount - 4); // Subtract initial 4 pieces
  }

  /**
   * Export opening statistics
   */
  getStatistics(): {
    totalPositions: number;
    maxDepth: number;
    averageWinRate: number;
    sequenceCount: number;
  } {
    const totalPositions = this._entries.size;
    let totalWinRate = 0;
    let entryCount = 0;

    for (const entries of this._entries.values()) {
      for (const entry of entries) {
        totalWinRate += entry.winRate;
        entryCount++;
      }
    }

    return {
      totalPositions,
      maxDepth: this._maxDepth,
      averageWinRate: entryCount > 0 ? totalWinRate / entryCount : 0,
      sequenceCount: this._sequences.length,
    };
  }

  /**
   * Validate opening book integrity
   */
  validateBook(): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Check for duplicate entries
    for (const [key, entries] of this._entries.entries()) {
      const positions = new Set<BoardPosition>();
      for (const entry of entries) {
        if (positions.has(entry.position)) {
          errors.push(`Duplicate position ${entry.position} in key ${key}`);
        }
        positions.add(entry.position);

        // Validate entry values
        if (entry.frequency < 0 || entry.frequency > 1) {
          errors.push(`Invalid frequency for position ${entry.position}: ${entry.frequency}`);
        }
        if (entry.winRate < 0 || entry.winRate > 1) {
          errors.push(`Invalid win rate for position ${entry.position}: ${entry.winRate}`);
        }
      }
    }

    // Validate sequences
    for (const sequence of this._sequences) {
      if (sequence.moves.length === 0) {
        errors.push(`Empty sequence: ${sequence.name}`);
      }
      for (const move of sequence.moves) {
        if (move < 0 || move >= 64) {
          errors.push(`Invalid move ${move} in sequence ${sequence.name}`);
        }
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }
}

/**
 * Default opening book instance
 */
export const defaultOpeningBook = new OpeningBook();

/**
 * Factory functions for creating opening books
 */
export const createOpeningBook = {
  /**
   * Create standard opening book
   */
  standard(): OpeningBook {
    return new OpeningBook(12);
  },

  /**
   * Create extended opening book with deeper analysis
   */
  extended(): OpeningBook {
    return new OpeningBook(16);
  },

  /**
   * Create minimal opening book for faster lookup
   */
  minimal(): OpeningBook {
    return new OpeningBook(8);
  },
};